/**
 * Form constants for tax filing UI.
 * Values aligned with backend schemas (do not import from backend - this file is the source of truth in the app).
 */

export const FORM_FILING_TYPE = {
  INDIVIDUAL: 'INDIVIDUAL',
  BUSINESS: 'BUSINESS',
} as const;

export type FormFilingType = (typeof FORM_FILING_TYPE)[keyof typeof FORM_FILING_TYPE];

export const PERSON_TYPE = {
  TAXPAYER: 'TAXPAYER',
  SPOUSE: 'SPOUSE',
  DEPENDENT: 'DEPENDENT',
} as const;

export type PersonType = (typeof PERSON_TYPE)[keyof typeof PERSON_TYPE];

export const US_STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC',
] as const;

export type USStateCode = (typeof US_STATE_CODES)[number];

export const RELATION_WITH_TAXPAYER = {
  SON: 'SON',
  DAUGHTER: 'DAUGHTER',
  FOSTER_CHILD: 'FOSTER CHILD',
  GRANDCHILD: 'GRANDCHILD',
  STEPCHILD: 'STEPCHILD',
  GRANDPARENT: 'GRANDPARENT',
  PARENT: 'PARENT',
  BROTHER: 'BROTHER',
  HALF_BROTHER: 'HALF BROTHER',
  STEPBROTHER: 'STEPBROTHER',
  SISTER: 'SISTER',
  HALF_SISTER: 'HALF SISTER',
  STEP_SISTER: 'STEP SISTER',
  AUNT: 'AUNT',
  UNCLE: 'UNCLE',
  NEPHEW: 'NEPHEW',
  NIECE: 'NIECE',
  NONE: 'NONE',
  OTHER: 'OTHER',
} as const;

export type RelationWithTaxpayer = (typeof RELATION_WITH_TAXPAYER)[keyof typeof RELATION_WITH_TAXPAYER];

export const MARITAL_STATUS = {
  SINGLE: 'SINGLE',
  MARRIED: 'MARRIED',
  DIVORCED: 'DIVORCED',
  WIDOWED: 'WIDOWED',
} as const;

export type MaritalStatus = (typeof MARITAL_STATUS)[keyof typeof MARITAL_STATUS];

export const COVERAGE_SOURCE = {
  EMPLOYER: 'EMPLOYER',
  SELF: 'SELF',
  MARKETPLACE: 'MARKETPLACE',
} as const;

export type CoverageSource = (typeof COVERAGE_SOURCE)[keyof typeof COVERAGE_SOURCE];

export const HEALTH_FORM_TYPE = {
  FORM_1095_A: '1095-A',
  FORM_1095_B: '1095-B',
  FORM_1095_C: '1095-C',
} as const;

export type HealthFormType = (typeof HEALTH_FORM_TYPE)[keyof typeof HEALTH_FORM_TYPE];

export const TAXPAYER_ID_TYPE = {
  SSN: 'SSN',
  EIN: 'EIN',
} as const;

export type TaxpayerIdType = (typeof TAXPAYER_ID_TYPE)[keyof typeof TAXPAYER_ID_TYPE];

export const ACCOUNTING_METHOD = {
  CASH: 'CASH',
  ACCRUAL: 'ACCRUAL',
} as const;

export type AccountingMethod = (typeof ACCOUNTING_METHOD)[keyof typeof ACCOUNTING_METHOD];

export const MILEAGE_METHOD = {
  STANDARD: 'STANDARD',
  ACTUAL: 'ACTUAL',
} as const;

export type MileageMethod = (typeof MILEAGE_METHOD)[keyof typeof MILEAGE_METHOD];

export const BUSINESS_TYPE = {
  SOLE_PROPRIETOR: 'SOLE_PROPRIETOR',
  SINGLE_MEMBER_LLC: 'SINGLE_MEMBER_LLC',
  PARTNERSHIP: 'PARTNERSHIP',
  S_CORP: 'S_CORP',
  C_CORP: 'C_CORP',
} as const;

export type BusinessType = (typeof BUSINESS_TYPE)[keyof typeof BUSINESS_TYPE];

/** Individual filing status (1040) */
export const INDIVIDUAL_FILING_STATUS = {
  SINGLE: 'SINGLE',
  MARRIED_FILING_JOINTLY: 'MARRIED_FILING_JOINTLY',
  MARRIED_FILING_SEPARATELY: 'MARRIED_FILING_SEPARATELY',
  HEAD_OF_HOUSEHOLD: 'HEAD_OF_HOUSEHOLD',
  QUALIFYING_WIDOW: 'QUALIFYING_WIDOW',
} as const;

export type IndividualFilingStatus = (typeof INDIVIDUAL_FILING_STATUS)[keyof typeof INDIVIDUAL_FILING_STATUS];
